// src/hooks/useWritingActivity.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet, apiPost } from '../api';
import { WritingActivity } from '../utils/writingMetrics';

const EMPTY: WritingActivity = { startTotal: null, days: {}, dayStarts: {}, dailyWordGoal: null };
const POLL_MS = 30_000;

export interface UseWritingActivityResult {
  activity: WritingActivity;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  saveGoal: (goal: number) => Promise<void>;
}

export function useWritingActivity(bookId: string | null): UseWritingActivityResult {
  const [activity, setActivity] = useState<WritingActivity>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bookRef = useRef<string | null>(bookId);
  bookRef.current = bookId;

  const fetchActivity = useCallback(async () => {
    const id = bookRef.current;
    if (!id) return;
    try {
      const res = await apiGet(`/books/${id}/writing-activity`);
      const data = (await res.body.json()) as unknown as WritingActivity;
      if (bookRef.current !== id) return; // bookId changed mid-flight — drop stale response
      setActivity({
        startTotal: data.startTotal ?? null,
        days: data.days ?? {},
        dayStarts: data.dayStarts ?? {},
        dailyWordGoal: data.dailyWordGoal ?? null,
      });
      setError(null);
    } catch {
      if (bookRef.current === id) setError('Failed to load writing activity.');
    }
  }, []);

  // Initial load + reload when the book changes.
  useEffect(() => {
    if (!bookId) { setActivity(EMPTY); setError(null); return; }
    setLoading(true);
    setActivity(EMPTY);
    setError(null);
    fetchActivity().finally(() => setLoading(false));
  }, [bookId, fetchActivity]);

  // Poll every 30s while the tab is visible; refetch on focus / visibility change.
  useEffect(() => {
    if (!bookId) return;
    let timer: ReturnType<typeof setInterval> | undefined;
    const stop = () => { if (timer) clearInterval(timer); timer = undefined; };
    const onVisible = () => { if (document.visibilityState === 'visible') fetchActivity(); };
    const onFocus = () => fetchActivity();

    timer = setInterval(() => {
      if (document.visibilityState === 'visible') fetchActivity();
    }, POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [bookId, fetchActivity]);

  const saveGoal = useCallback(async (goal: number) => {
    const id = bookRef.current;
    if (!id) return;
    setActivity(a => ({ ...a, dailyWordGoal: goal })); // optimistic
    try {
      await apiPost(`/books/${id}/writing-activity`, { dailyWordGoal: goal });
    } finally {
      fetchActivity();
    }
  }, [fetchActivity]);

  return { activity, loading, error, refetch: fetchActivity, saveGoal };
}
